/**
 * Canonical analytics path representation.
 * Must not change browser routing — sanitizes values sent to GA4 only.
 */

const CONNECT_TOKEN_PATH =
  /^(\/play\/connect\/)([^/]+)(\/.*)?$/

export function isDevPreviewAnalyticsPath(
  pathname: string | null | undefined
): boolean {
  const path = analyticsPathname(pathname)
  return path === '/dev-preview' || path.startsWith('/dev-preview/')
}

/**
 * Normalize a pathname (or full URL) for GA4 page_path.
 * Connect bearer tokens become `:token`; V2 virtual screens pass through.
 */
export function sanitizeAnalyticsPath(pathname: string | null | undefined): string {
  const path = analyticsPathname(pathname)
  const match = path.match(CONNECT_TOKEN_PATH)
  if (!match) return path
  const suffix = match[3] ?? ''
  if (match[2] === 'resume') {
    return `/play/connect/resume${suffix}`
  }
  return `/play/connect/:token${suffix}`
}

/**
 * Origin + sanitized pathname, with search and hash omitted (Phase 0).
 * Prevents raw connect tokens from appearing in page_location.
 */
export function sanitizeAnalyticsLocation(
  href: string | null | undefined
): string {
  const raw = (href ?? '').trim()
  if (!raw) return ''
  try {
    const url = new URL(raw)
    return `${url.origin}${sanitizeAnalyticsPath(url.pathname)}`
  } catch {
    return sanitizeAnalyticsPath(raw)
  }
}

function analyticsPathname(input: string | null | undefined): string {
  const raw = (input ?? '').trim()
  if (!raw) return ''
  try {
    if (/^https?:\/\//i.test(raw)) {
      return new URL(raw).pathname || '/'
    }
  } catch {
    /* fall through */
  }
  const withoutQuery = raw.split('?')[0]?.split('#')[0] ?? raw
  return withoutQuery || '/'
}
